import * as React from 'react';
import {
    createSaveSearchKeyBySearchParams,
    deleteSavedSearch,
    getSaveSearchState,
    submitSaveSearch,
} from '@/redux/modules/saveSearch';
import { getSaveSearchCheckStateByKey, submitIsSavedSearchCheck } from '@/redux/modules/saveSearchCheck';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthToken } from '@/redux/modules/user';
import isEqual from 'react-fast-compare';
import SaveSearch from './SaveSearch';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import usePrevious from '@/hooks/usePrevious';

type Props = {
    allSearchParams?: SearchParamsType;
    className?: string;
    primary: boolean;
    saveSearchParams: SearchParamsType;
    small?: boolean;
    tooltipMessage?: React.ReactNode | string;
};

const SaveSearchContainer = ({
    allSearchParams,
    className,
    primary,
    saveSearchParams,
    small,
    tooltipMessage,
}: Props) => {
    const dispatch = useDispatch();

    const authToken = useSelector((state) => getAuthToken(state), isEqual);
    const saveSearchKey = useSelector(() => createSaveSearchKeyBySearchParams(saveSearchParams), isEqual);
    const saveSearchCheckState = useSelector((state) => getSaveSearchCheckStateByKey(state, saveSearchKey));
    const saveSearchState = useSelector((state) => getSaveSearchState(state), isEqual);

    const { isSavedSearch } = saveSearchCheckState;

    React.useEffect(() => {
        if (authToken) {
            dispatch(submitIsSavedSearchCheck(saveSearchParams));
        }
    }, [authToken, dispatch, saveSearchParams]);

    const prevAuthToken = usePrevious(authToken);
    React.useEffect(() => {
        if (!prevAuthToken && authToken) {
            dispatch(submitIsSavedSearchCheck(saveSearchParams));
        }
    }, [authToken, dispatch, prevAuthToken, saveSearchParams]);

    const submitSaveSearchLocal = React.useCallback(() => {
        const sendSearchAlert = true;
        dispatch(submitSaveSearch(saveSearchParams, sendSearchAlert));
    }, [dispatch, saveSearchParams]);

    const deleteSavedSearchLocal = React.useCallback(() => {
        if (isSavedSearch) {
            dispatch(deleteSavedSearch(saveSearchParams));
        }
    }, [dispatch, isSavedSearch, saveSearchParams]);

    return (
        <SaveSearch
            allSearchParams={allSearchParams}
            className={className}
            deleteSavedSearch={deleteSavedSearchLocal}
            primary={primary}
            saveSearchCheckState={saveSearchCheckState}
            saveSearchState={saveSearchState}
            small={small}
            submitSaveSearch={submitSaveSearchLocal}
            tooltipMessage={tooltipMessage}
        />
    );
};

export default React.memo<Props>(SaveSearchContainer, isEqual);
